import h from 'virtual-dom/h';
import SectionBuilder from "./builders/sectionBuilder";
import { shouldEnableInputBox } from "./inputBoxLocking/index";
import {DefaultSettings, settings} from '../../../settings/settings-util';
import {VChild, VTree, VNode} from "virtual-dom";
import {HistoryMessage, HistoryMessageAlme} from "../../../types/history";
import {Reducer, Store} from "redux";
import {AlmeState, defaultState} from "../../../wireup/AlmeState";
import {DisplayLinkSection} from "@alme/types/response/DisplayLinkSection";
import {htmlToVdom, Mutator} from "../../../types/rendering";


type StepOutIcon = { renderIcon: (url: string) => VNode | undefined };
type InlineLinkTuple = { matchedString: string; renderedLink: VNode };
type MutatorModule = { getMutator: () => Mutator };


export default class InlineLinksRenderer
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _sectionBuilder: SectionBuilder;


    //#region Public Interface

    constructor(passedSettings?: DefaultSettings,
                stepOutIcon: StepOutIcon | null = null,
                linkMutatorModules: MutatorModule[] = [])
    {
        this._settings = passedSettings ?? settings;

        let linkMutators = linkMutatorModules.reduce((mutators, module) => [...mutators, module.getMutator()], [] as Mutator[]);
        this._sectionBuilder = new SectionBuilder(this._settings, stepOutIcon, linkMutators);
    }

    static getDefaultSettings(): DefaultSettings
    {
        return settings;
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;

        this._sectionBuilder.initialize(store);
    }

    getReducer(): Reducer<AlmeState>
    {
        return (state: AlmeState = defaultState): AlmeState => state;
    }

    getRenderer(): (message: HistoryMessage) => VChild
    {
        return (message: HistoryMessage): VChild =>
        {
            const almeMessage = message as HistoryMessageAlme;
            const postMarkdownHtml = (almeMessage?.extensionData?.postMarkdownHtml as string);

            if (!postMarkdownHtml) { return []; }

            const inlineLinkPattern = /\[([^\][]*)]\((?:(\d{1,2}),)?(\d{1,2})?\)/ig;
            const inlineLinkTuples = [] as InlineLinkTuple[];

            //NOTE: Cancer IE doesn't support string.matchAll() 🤬
            let match;
            while ((match = inlineLinkPattern.exec(postMarkdownHtml)) !== null)
            {
                const displayText = match[1];
                const sectionIndex = parseInt(match[2] ?? "0");
                const linkIndex = parseInt(match[3]);

                const section = this.getDisplayLinkSection(almeMessage, sectionIndex);
                const displayLink = section.Links[linkIndex];

                if (!displayLink) { return []; }
                if (displayText) { displayLink.DisplayText = displayText; }

                const shouldEnable = shouldEnableInputBox(settings, this._store);

                const renderedDisplayLink = this._sectionBuilder.buildLink(displayLink, shouldEnable, almeMessage, linkIndex);
                const renderedLink = h('div', { className: this._settings.classNames.displayInlineClassName }, this.removeIntermediaryDiv(renderedDisplayLink));

                displayLink.Metadata.inlineLink = true;

                inlineLinkTuples.push({ matchedString: match[0], renderedLink });
            }

            const tempHtmlString = inlineLinkTuples.reduce((htmlString, tuple) =>
                htmlString.replace(tuple.matchedString, '<replace></replace>'), postMarkdownHtml);

            const vdomElements = htmlToVdom(tempHtmlString);
            const vTreeArray: VTree[] = Array.isArray(vdomElements) ? vdomElements : [vdomElements];
            almeMessage.extensionData.postMarkdownVdomElements = this.insertRenderedLinks(vTreeArray, inlineLinkTuples);

            return [];
        };
    }

    //#endregion



    //#region Inline Link Methods

    removeIntermediaryDiv(linkArray: VNode[]): VNode[]
    {
        if (linkArray.length === 0) { return []; }

        return linkArray.flatMap(link => link.children.map(child => child as VNode));
    }

    insertRenderedLinks(vTree: VTree[], inlineLinkTuples: InlineLinkTuple[]): VTree[]
    {
        return vTree.reduce((tree, node) =>
        {
            if (node.type !== 'VirtualNode') { return tree.concat(node); }

            const vNode = node as VNode;

            if (vNode.children.length > 0)
            {
                vNode.children = this.insertRenderedLinks(vNode.children, inlineLinkTuples);

                return tree.concat(vNode);
            }

            return tree.concat(vNode.tagName === 'replace' ? inlineLinkTuples.shift()?.renderedLink ?? vNode : vNode);
        }, [] as VTree[]);
    }

    getDisplayLinkSection(message: HistoryMessageAlme, index: number): DisplayLinkSection
    {
        return message.displayLinkCollection?.Sections[index] as DisplayLinkSection;
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/message-renderer-display-links/src/message-renderer-inline-links.ts